import { memo, useMemo } from "react";
import { View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
	SharedValue,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";
import cursor from "src/constants/cursor";
import SystemInfo from "src/constants/SystemInfo";
import useDevice from "src/hooks/useDevice";
import useStyle from "src/hooks/useStyle";
import { radiusSmall } from "src/styles/radius";
import { paddingLarge } from "src/styles/spacing";
import { ImageCropperMaxScale, ImageCropperMinScale } from "../constants";
import { calculateCropFromScaleWorklet } from "../helpers/calculateCropWorklets";
interface ScaleSliderProps {
	width: number;
	cropSV: SharedValue<{
		scale: number;
		x: number;
		y: number;
	}>;
	inside: {
		width: number;
		height: number;
	};
}

const CircleSize = 16;
const HorizontalHitSlop = CircleSize / 2;
const SliderHeight = 4;
const ImageCropperScaleRange = ImageCropperMaxScale - ImageCropperMinScale;
const ScaleSliderContent = ({ width, cropSV, inside }: ScaleSliderProps) => {
	const sliderWidth = useMemo(() => Math.max(0.2 * width, 100), [width]);
	const { theme } = useStyle();
	const isGrabbingSV = useSharedValue(false);
	const gesture = useMemo(() => {
		const updateCropSV = (event: { x: number }) => {
			"worklet";
			const boundedX = Math.max(0, Math.min(event.x - HorizontalHitSlop, sliderWidth));
			cropSV.value = calculateCropFromScaleWorklet({
				targetScale:
					(boundedX / sliderWidth) * ImageCropperScaleRange + ImageCropperMinScale,
				crop: cropSV.value,
				inside,
			});
		};
		return Gesture.Pan()
			.onBegin((event) => {
				"worklet";
				isGrabbingSV.value = true;
				updateCropSV(event);
			})
			.onChange((event) => {
				"worklet";
				updateCropSV(event);
			})
			.onEnd(() => {
				"worklet";
				isGrabbingSV.value = false;
			})
			.minDistance(0);
	}, [isGrabbingSV, sliderWidth, inside, cropSV]);
	const animatedStyle = useAnimatedStyle(() => {
		return {
			transform: [
				{
					translateX:
						(sliderWidth * (cropSV.value.scale - ImageCropperMinScale)) /
						ImageCropperScaleRange,
				},
				{
					scale: withTiming(isGrabbingSV.value ? 1.2 : 1, { duration: 100 }),
				},
			],
			cursor: isGrabbingSV.value ? cursor.grabbing : cursor.pointer,
		};
	}, [cropSV, isGrabbingSV, sliderWidth]);

	return (
		<GestureDetector gesture={gesture}>
			{/* Hit area*/}
			<View
				style={{
					position: "absolute",
					width: sliderWidth + 2 * HorizontalHitSlop,
					height: CircleSize,
					right: paddingLarge,
					bottom: paddingLarge,
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{/* Slider */}
				<View
					style={{
						marginHorizontal: HorizontalHitSlop,
						borderRadius: radiusSmall,
						width: sliderWidth,
						height: SliderHeight,
						backgroundColor: "#FFFFFF66",
						justifyContent: "center",
						cursor: cursor.pointer,
					}}
				>
					<Animated.View
						testID="CropSlider"
						style={[
							{
								left: -CircleSize / 2,
								position: "absolute",
								width: CircleSize,
								height: CircleSize,
								borderRadius: CircleSize / 2,
								backgroundColor: "#FFFFFF",
							},
							animatedStyle,
						]}
					/>
				</View>
			</View>
		</GestureDetector>
	);
};

const ScaleSlider = (props: ScaleSliderProps) => {
	const { device } = useDevice();
	const shouldRender = useMemo(() => {
		switch (SystemInfo.OS) {
			case "ios":
			case "android":
			case "windows phone":
				return false;
		}
		if (device === "desktop") return true;
		return true;
	}, [device]);

	if (!shouldRender) return null;
	return <ScaleSliderContent {...props} />;
};
export default memo(ScaleSlider);
